import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { countryLabel, countryOptions } from "#shared/countries.ts";
import type { EntityDetail } from "#shared/schemas/entityDetail.ts";
import { cn } from "@web/lib/utils.ts";

/**
 * Artist Country — genre-like chips, one value max.
 * Selected badge · MB suggestion · + official ISO list.
 */
export function CountryChipRow({
  detail,
  disabled,
  onSet,
  onClear,
}: {
  detail: EntityDetail;
  disabled: boolean;
  onSet: (code: string) => void;
  onClear: () => void;
}) {
  const raw = detail.values.country;
  const selected =
    typeof raw === "string" && raw.trim().length > 0 ? raw.trim().toUpperCase() : null;
  const suggestion = !selected && detail.suggested_country
    ? detail.suggested_country.trim().toUpperCase()
    : null;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => countryOptions(), []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = options.filter((o) => o.code !== selected);
    if (!q) return base;
    return base.filter(
      (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
    );
  }, [options, query, selected]);

  useEffect(() => {
    if (!pickerOpen) return;
    setHighlight(0);
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setPickerOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickerOpen]);

  useEffect(() => {
    if (pickerOpen) inputRef.current?.focus();
  }, [pickerOpen]);

  if (detail.entity_type !== "artist") return null;

  function pick(code: string) {
    onSet(code);
    setPickerOpen(false);
    setQuery("");
  }

  return (
    <section className="space-y-1.5" aria-label="Country">
      <div className="flex flex-wrap items-center gap-1.5">
        {selected && (
          <span className="inline-flex items-center gap-1 rounded-full bg-panel px-2.5 py-1 text-xs text-text">
            {countryLabel(selected)}
            <button
              type="button"
              disabled={disabled}
              aria-label="Clear country"
              className="rounded-full p-0.5 text-faint hover:bg-row-hover hover:text-text disabled:opacity-50"
              onClick={() => onClear()}
            >
              <X className="size-3" />
            </button>
          </span>
        )}

        {suggestion && (
          <button
            type="button"
            disabled={disabled}
            title="From MusicBrainz — click to set"
            className="rounded-full border border-dashed border-line px-2.5 py-1 text-xs text-muted hover:border-accent hover:text-accent disabled:opacity-50"
            onClick={() => onSet(suggestion)}
          >
            {countryLabel(suggestion)}?
          </button>
        )}

        <div ref={rootRef} className="relative">
          <button
            type="button"
            disabled={disabled}
            aria-label="Set country"
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            className="inline-flex size-7 items-center justify-center rounded-full border border-dashed border-line text-faint hover:border-muted hover:text-text disabled:opacity-50"
            onClick={() => {
              setPickerOpen((o) => !o);
              setQuery("");
            }}
          >
            <Plus className="size-3.5" />
          </button>

          {pickerOpen && (
            <div className="absolute top-full left-0 z-20 mt-1 w-64 rounded-md border border-line bg-panel-elevated p-2 shadow-lg">
              <input
                ref={inputRef}
                type="search"
                role="combobox"
                aria-expanded
                aria-controls={listId}
                aria-autocomplete="list"
                aria-label="Search countries"
                value={query}
                disabled={disabled}
                placeholder="Search countries…"
                className="h-8 w-full rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlight(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setPickerOpen(false);
                    setQuery("");
                    return;
                  }
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlight((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlight((i) => Math.max(i - 1, 0));
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const opt = filtered[highlight];
                    if (opt) pick(opt.code);
                  }
                }}
              />
              <ul
                id={listId}
                role="listbox"
                className="mt-1 max-h-48 overflow-y-auto"
              >
                {filtered.length === 0 ? (
                  <li className="px-2 py-1.5 text-xs text-faint">No matches</li>
                ) : (
                  filtered.map((opt, i) => (
                    <li key={opt.code} role="option" aria-selected={highlight === i}>
                      <button
                        type="button"
                        disabled={disabled}
                        className={cn(
                          "flex w-full items-baseline justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-row-hover",
                          highlight === i ? "bg-row-hover text-text" : "text-muted",
                        )}
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => pick(opt.code)}
                      >
                        <span className="min-w-0 truncate">{opt.name}</span>
                        <span className="shrink-0 text-xs text-faint">{opt.code}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
